<template>
  <AppMainDrawer
    :use-wide-display-breakpoint="false"
    center-content-on-close
  >
    <template #drawer>
      <DrawerStyled>
        <template #drawer-content>
          <ExpandableFilters />
        </template>
      </DrawerStyled>
    </template>
    <template #content>
      <div
        class="insights-page-container"
        data-png-export="insights"
      >
        <InsightsTopBar
          v-model="filterDrawerStore.open"
          class="w-100"
        />
        <div class="insights-page">
          <router-view
            v-slot="{ Component }"
            @track-page-load="$emit('track-page-load', $event)"
            @insights-tracking-event="handleTrackingEvent($event)"
          >
            <component :is="Component" />
          </router-view>
        </div>
      </div>
    </template>
  </AppMainDrawer>
</template>

<script setup>
import {
  computed, watch, onMounted, onBeforeUnmount, ref, markRaw,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import AppMainDrawer from '@/components/shared/AppMainDrawer.vue';
import InsightsTopBar from '@/components/insights/InsightsTopBar.vue';
import DrawerStyled from '@/components/shared/DrawerStyled.vue';
import ExpandableFilters from '@/components/filter/ExpandableFilters.vue';
import { useTracking } from '@/stores/tracking/tracking';
import { tooltipKeys } from '@/helpers/constants/tooltipKeys';
import { useDatePicker } from '@/stores/filters/datePicker';
import { useTooltips } from '@/stores/insights/tooltips';
import { useInsights } from '@/stores/insights/insights';
import { storeToRefs } from 'pinia';
import { useAppLoading } from '@/stores/app/loading';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useFilterDrawer } from '@/stores/filters/filterDrawer';
import { filtersTooltipKeys } from '@/helpers/constants/filtersTooltipKeys';
import DpplSmileIcon from '@/assets/icon-dppl-smile.svg?component';

defineEmits(['track-page-load']);

const route = useRoute();
const router = useRouter();

const { pageLoading, filtersLoading } = storeToRefs(useAppLoading());
const { activeSnackbar } = storeToRefs(useAppSnackbar());

const { loadTooltips } = useTooltips();
const { resetDateRange, setDatePickerVariant } = useDatePicker();
const { setShowInternalDefault } = useInsights();
const { addInfoSnackbar } = useAppSnackbar();
const { addSnackbar, removeActiveSnackbar } = useAppSnackbar();

const filterDrawerStore = useFilterDrawer();

const loadingTimeout = ref(null);

const getRouteNameForTooltips = computed(() => tooltipKeys[route.name] || []);

watch(getRouteNameForTooltips, (newValue) => {
  loadTooltips([...filtersTooltipKeys, ...newValue]);
}, { immediate: true });

let snackbarMinTimeout = null;
watch(
  () => pageLoading.value || filtersLoading.value,
  (newValue) => {
    if (newValue) {
      // Start a 10 second timer when loading starts
      if (!loadingTimeout.value) {
        loadingTimeout.value = setTimeout(() => {
          addSnackbar({
            color: 'warning',
            text: 'Larger datasets may cause reports or filters to load more slowly. Good things are worth the wait!',
            iconComponent: markRaw(DpplSmileIcon),
            timeout: -1, // Keep snackbar open until manually closed
          });
          loadingTimeout.value = null;
        }, 7000);
      }
    } else {
      // Loading finished
      clearTimeout(loadingTimeout.value);
      loadingTimeout.value = null;

      if (activeSnackbar.value) {
        // If snackbar is open, keep it open for 3 more seconds
        if (!snackbarMinTimeout) {
          snackbarMinTimeout = setTimeout(() => {
            removeActiveSnackbar();
            snackbarMinTimeout = null;
          }, 3000);
        }
      }
    }
  },
);

const handleTrackingEvent = ({ action, args }) => {
  useTracking()[action](args);
};

onMounted(async () => {
  if (route.query?.legacy === 'true') {
    addInfoSnackbar({
      text: `Attention!
          The URL for this page has recently changed. Please update
          your bookmark or saved URL to include the URL you are
          currently viewing. Contact support@datapeople.io if you
          have any questions.`,
    });

    await router.replace({
      query: { ...route.query, legacy: undefined },
    }).catch(() => false);
  }
});

onBeforeUnmount(() => {
  resetDateRange();
  setDatePickerVariant();
  setShowInternalDefault(true);
});
</script>

<script>
export default { name: 'InsightsView' };
</script>

<style scoped lang="less">
.insights-page-container {
  max-width: 1400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.insights-page {
  text-align: left;
  width: 100%;
  /* padding bottom is needed to ensure the email image report shows the filters */
  padding: 0px 24px 40px 24px;
}
</style>
